import * as THREE from 'three';
import type { BadgeQuad, Measure } from './badgeLayout';

/**
 * Every floating badge (`badgeLayout.ts`) drawn as camera-facing quads in one
 * instanced draw: pills shaded in the fragment shader (a rounded-rect
 * distance), glyphs sampled from a `CanvasTexture` atlas that gains a glyph the
 * first time one is shown. Sizes are CSS pixels, as the `Html` chips they
 * replace were: a badge stays the same size on screen at any zoom, and draws
 * over the scene (no depth test). The activity bob runs in the shader.
 *
 * `setBadges` lays out the instances (per tick, skipped when nothing
 * changed); `setAnchor` moves one badge's anchor (per frame, for units).
 */

export interface BadgeSpec {
  /** Stable id, for `setAnchor` and the bob's phase. */
  key: string;
  x: number;
  y: number;
  z: number;
  quads: BadgeQuad[];
}

/** Atlas glyphs are drawn at this font size; quads scale them down. */
const REF = 48;
const LINE = 1.25;
/** Space round each atlas cell, so mipmaps don't bleed neighbours in. */
const PAD = 8;
const ATLAS_W = 1024;
const ATLAS_H = 512;
/** Bob height in px (the old CSS `activity-bob`: 2px up and back over 2 s). */
const BOB_PX = 2;

const FONT_FALLBACK = '"Helvetica Neue", "Inter Tight", Helvetica, Arial, sans-serif';

const VERTEX = /* glsl */ `
attribute vec3 aAnchor;
attribute vec4 aRect;
attribute vec4 aUv;
attribute vec4 aTint;
attribute vec3 aStyle;
uniform vec2 uViewport;
uniform float uTime;
varying vec2 vUv;
varying vec2 vLocal;
varying vec2 vSize;
varying vec4 vTint;
varying float vKind;
void main() {
  vec4 clip = projectionMatrix * modelViewMatrix * vec4(aAnchor, 1.0);
  vec2 px = aRect.xy + position.xy * aRect.zw;
  px.y += aStyle.y * 0.5 * (1.0 - cos((uTime + aStyle.z) * 3.14159265));
  clip.xy += px * 2.0 / uViewport * clip.w;
  gl_Position = clip.w > 0.0 ? clip : vec4(2.0, 2.0, 2.0, 1.0);
  vUv = mix(aUv.xy, aUv.zw, position.xy + 0.5);
  vLocal = position.xy * aRect.zw;
  vSize = aRect.zw;
  vTint = aTint;
  vKind = aStyle.x;
}
`;

// Pills: --color-surface-strong with a 1px --color-line border.
const FRAGMENT = /* glsl */ `
uniform sampler2D uMap;
varying vec2 vUv;
varying vec2 vLocal;
varying vec2 vSize;
varying vec4 vTint;
varying float vKind;
void main() {
  if (vKind < 0.5) {
    vec4 t = texture2D(uMap, vUv) * vTint;
    if (t.a < 0.01) discard;
    gl_FragColor = t;
    return;
  }
  vec2 halfSize = vSize * 0.5;
  float r = min(halfSize.x, halfSize.y);
  vec2 q = abs(vLocal) - halfSize + r;
  float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
  float inside = 1.0 - smoothstep(-0.5, 0.5, d);
  vec4 fill = vec4(12.0 / 255.0, 14.0 / 255.0, 20.0 / 255.0, 0.72);
  float line = vKind > 1.5 ? 0.16 * smoothstep(-1.5, -0.5, d) : 0.0;
  float a = line + fill.a * (1.0 - line);
  vec3 rgb = (vec3(line) + fill.rgb * fill.a * (1.0 - line)) / a;
  gl_FragColor = vec4(rgb, a * inside);
}
`;

const FLOATS = { aAnchor: 3, aRect: 4, aUv: 4, aTint: 4, aStyle: 3 } as const;
type AttrName = keyof typeof FLOATS;

interface Cell {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  /** Advance at `REF` px. */
  advance: number;
}

/** A stable bob phase (0–2 s) from a key, so neighbouring badges don't bob in step. */
function phaseOf(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  return ((h >>> 0) % 2000) / 1000;
}

const scratchColor = new THREE.Color();

export class BadgeBatch {
  readonly mesh: THREE.Mesh;
  private readonly geometry = new THREE.InstancedBufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private readonly attrs = {} as Record<AttrName, THREE.InstancedBufferAttribute>;
  private capacity = 0;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly texture: THREE.CanvasTexture;
  private readonly cells = new Map<string, Cell>();
  private readonly widths = new Map<string, number>();
  private readonly family: string;
  /** Shelf packing cursor. */
  private shelfX = 0;
  private shelfY = 0;
  private shelfH = 0;
  /** Each badge's instance range, for `setAnchor`. */
  private readonly ranges = new Map<string, { start: number; count: number }>();
  private signature = '';

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = ATLAS_W;
    this.canvas.height = ATLAS_H;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('BadgeBatch: no 2D canvas');
    this.ctx = ctx;
    this.family =
      (typeof getComputedStyle === 'function' && getComputedStyle(document.body).fontFamily) ||
      FONT_FALLBACK;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.minFilter = THREE.LinearMipmapLinearFilter;
    this.texture.magFilter = THREE.LinearFilter;

    const plane = new THREE.PlaneGeometry(1, 1);
    this.geometry.setIndex(plane.index);
    this.geometry.setAttribute('position', plane.getAttribute('position'));
    this.grow(64);
    this.geometry.instanceCount = 0;

    this.material = new THREE.ShaderMaterial({
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      uniforms: {
        uMap: { value: this.texture },
        uViewport: { value: new THREE.Vector2(1, 1) },
        uTime: { value: 0 },
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    // Over the scene, as the DOM badges were; never takes a click.
    this.mesh.renderOrder = 10;
    this.mesh.raycast = () => {};
    // The stand-in for glyphs that no longer fit (`cell`), drawn while there's room.
    this.cell('?', true);
  }

  /** A glyph's advance in CSS px at `font` px (for `badgeLayout`). */
  readonly measure: Measure = (glyph, font, bold) => {
    const key = `${bold ? 'b' : 'n'}${font}|${glyph}`;
    let w = this.widths.get(key);
    if (w === undefined) {
      this.ctx.font = this.font(font, bold);
      w = this.ctx.measureText(glyph).width;
      this.widths.set(key, w);
    }
    return w;
  };

  /** Viewport size in CSS px and the clock, per frame. */
  frame(width: number, height: number, time: number): void {
    (this.material.uniforms.uViewport.value as THREE.Vector2).set(width, height);
    this.material.uniforms.uTime.value = time;
  }

  /** Replace every badge (skipped when they read the same as last time, anchors aside). */
  setBadges(badges: readonly BadgeSpec[]): void {
    const signature = badges
      .map(
        (b) =>
          `${b.key}:${b.quads.map((q) => (q.kind === 'glyph' ? q.glyph + q.x.toFixed(1) : q.w)).join(',')}`
      )
      .join(';');
    if (signature === this.signature) {
      for (const b of badges) this.setAnchor(b.key, b.x, b.y, b.z);
      return;
    }
    this.signature = signature;
    const total = badges.reduce((n, b) => n + b.quads.length, 0);
    if (total > this.capacity) this.grow(Math.max(total, this.capacity * 2));
    this.ranges.clear();
    const a = this.attrs;
    let i = 0;
    for (const badge of badges) {
      this.ranges.set(badge.key, { start: i, count: badge.quads.length });
      const phase = phaseOf(badge.key);
      for (const q of badge.quads) {
        a.aAnchor.setXYZ(i, badge.x, badge.y, badge.z);
        a.aStyle.setXYZ(i, q.kind === 'pill' ? (q.border ? 2 : 1) : 0, q.bob ? BOB_PX : 0, phase);
        if (q.kind === 'pill') {
          a.aRect.setXYZW(i, q.x, q.y, q.w, q.h);
          a.aUv.setXYZW(i, 0, 0, 0, 0);
          a.aTint.setXYZW(i, 1, 1, 1, 1);
        } else {
          const cell = this.cell(q.glyph, q.bold);
          // The cell holds the glyph's advance box plus padding at REF px.
          const sx = cell.advance > 0 ? q.w / cell.advance : q.font / REF;
          const sy = q.font / REF;
          a.aRect.setXYZW(i, q.x, q.y, (cell.advance + 2 * PAD) * sx, (REF * LINE + 2 * PAD) * sy);
          a.aUv.setXYZW(i, cell.u0, cell.v0, cell.u1, cell.v1);
          // Raw sRGB, like the atlas and the pill literals: the shader writes
          // straight to the sRGB canvas, so no conversion to linear here.
          scratchColor.setStyle(q.tint, THREE.NoColorSpace);
          a.aTint.setXYZW(i, scratchColor.r, scratchColor.g, scratchColor.b, 1);
        }
        i++;
      }
    }
    this.geometry.instanceCount = i;
    for (const attr of Object.values(a)) {
      attr.clearUpdateRanges();
      if (i > 0) attr.addUpdateRange(0, i * attr.itemSize);
      attr.needsUpdate = true;
    }
  }

  /** Move badge `key` (a moving unit's), if shown. */
  setAnchor(key: string, x: number, y: number, z: number): void {
    const range = this.ranges.get(key);
    if (!range) return;
    const anchor = this.attrs.aAnchor;
    if (
      anchor.getX(range.start) === x &&
      anchor.getY(range.start) === y &&
      anchor.getZ(range.start) === z
    )
      return;
    for (let i = range.start; i < range.start + range.count; i++) anchor.setXYZ(i, x, y, z);
    anchor.clearUpdateRanges();
    anchor.addUpdateRange(0, this.geometry.instanceCount * 3);
    anchor.needsUpdate = true;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.texture.dispose();
  }

  private font(px: number, bold: boolean): string {
    return `${bold ? 700 : 400} ${px}px ${this.family}`;
  }

  /** The glyph's atlas cell, drawn on first use. */
  private cell(glyph: string, bold: boolean): Cell {
    const key = `${bold ? 'b' : 'n'}|${glyph}`;
    const known = this.cells.get(key);
    if (known) return known;
    const ctx = this.ctx;
    ctx.font = this.font(REF, bold);
    const advance = ctx.measureText(glyph).width;
    const w = Math.ceil(advance) + 2 * PAD;
    const h = Math.ceil(REF * LINE) + 2 * PAD;
    if (this.shelfX + w > ATLAS_W) {
      this.shelfX = 0;
      this.shelfY += this.shelfH;
      this.shelfH = 0;
    }
    if (this.shelfY + h > ATLAS_H) {
      console.warn(`BadgeBatch: atlas full, "${glyph}" not drawn`);
      // A '?' already in the atlas stands in; blank if there is none.
      const stand = this.cells.get(`${bold ? 'b' : 'n'}|?`);
      const fallback = stand ?? { u0: 0, v0: 0, u1: 0, v1: 0, advance };
      this.cells.set(key, fallback);
      return fallback;
    }
    const x = this.shelfX;
    const y = this.shelfY;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(glyph, x + w / 2, y + h / 2);
    this.shelfX += w;
    this.shelfH = Math.max(this.shelfH, h);
    this.texture.needsUpdate = true;
    // The texture is flipped on upload (flipY): v runs up from the canvas bottom.
    const cell = {
      u0: x / ATLAS_W,
      v0: 1 - (y + h) / ATLAS_H,
      u1: (x + w) / ATLAS_W,
      v1: 1 - y / ATLAS_H,
      advance,
    };
    this.cells.set(key, cell);
    return cell;
  }

  private grow(capacity: number): void {
    // Frees the old attributes' GPU buffers; the renderer uploads the new ones.
    this.geometry.dispose();
    for (const name of Object.keys(FLOATS) as AttrName[]) {
      const size = FLOATS[name];
      const attr = new THREE.InstancedBufferAttribute(new Float32Array(capacity * size), size);
      attr.setUsage(THREE.DynamicDrawUsage);
      const old = this.attrs[name];
      if (old) (attr.array as Float32Array).set(old.array as Float32Array);
      this.attrs[name] = attr;
      this.geometry.setAttribute(name, attr);
    }
    this.capacity = capacity;
  }
}
